// src/pages/finance/InvoicesPage.tsx
import {
  AlertCircle,
  Banknote,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Receipt,
  RefreshCw,
  Search,
  TrendingUp,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_CONFIG, fetchApp } from "../../../services/apiService";

type InvoiceStatus = "PENDING" | "PAID" | "EXPIRED" | "CANCELLED";

interface Invoice {
  id: number;
  invoice_number: string;
  billing_account_id: number;
  secret_id: number;
  secret_username?: string | null;
  plan_name?: string | null;
  status: InvoiceStatus;
  currency: string;
  total_amount_cents: number;
  paid_amount_cents: number;
  period_start?: string | null;
  period_end?: string | null;
  created_at?: string | null;
}

const formatKES = (cents: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(Math.round((cents || 0) / 100))}`;

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const s = String(status || "").toUpperCase();

  const configs: Record<
    string,
    { cls: string; icon: React.ReactNode; label: string }
  > = {
    PAID: {
      cls: "border-emerald-200 bg-emerald-100 text-emerald-800",
      icon: <CheckCircle2 className="h-3 w-3" />,
      label: "Paid",
    },
    PENDING: {
      cls: "border-amber-200 bg-amber-100 text-amber-800",
      icon: <Clock className="h-3 w-3" />,
      label: "Pending",
    },
    EXPIRED: {
      cls: "border-rose-200 bg-rose-100 text-rose-700",
      icon: <AlertCircle className="h-3 w-3" />,
      label: "Expired",
    },
    CANCELLED: {
      cls: "border-slate-300 bg-slate-200 text-slate-600",
      icon: <XCircle className="h-3 w-3" />,
      label: "Cancelled",
    },
  };

  const cfg = configs[s] ?? {
    cls: "border-slate-300 bg-slate-200 text-slate-600",
    icon: null,
    label: s || "Unknown",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${cfg.cls}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            i === 0 ? "w-28" : i === 3 ? "w-20 rounded-full" : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-32 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>
      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const STATUS_TABS = [
  { label: "All", value: "ALL" },
  { label: "Paid", value: "PAID" },
  { label: "Pending", value: "PENDING" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");

  const loadInvoices = async () => {
    setError(null);

    if (invoices.length === 0) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const res = await fetchApp(`${API_CONFIG.baseUrl}/finances/invoices`);
      setInvoices(Array.isArray(res) ? res : res?.items || res?.data || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load invoices.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    let list = invoices;

    if (statusFilter !== "ALL") {
      list = list.filter((invoice) => invoice.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();

      list = list.filter((invoice) =>
        [
          invoice.invoice_number,
          String(invoice.id),
          String(invoice.billing_account_id),
          invoice.secret_username || "",
          invoice.plan_name || "",
        ].some((value) => value.toLowerCase().includes(q)),
      );
    }

    return list;
  }, [invoices, search, statusFilter]);

  const stats = useMemo(() => {
    const totalPaidCents = invoices.reduce(
      (sum, invoice) => sum + Number(invoice.paid_amount_cents || 0),
      0,
    );

    const totalInvoiceCents = invoices.reduce(
      (sum, invoice) => sum + Number(invoice.total_amount_cents || 0),
      0,
    );

    const openInvoices = invoices.filter(
      (invoice) => invoice.status !== "PAID",
    );

    const openBalanceCents = openInvoices.reduce((sum, invoice) => {
      const balance =
        Number(invoice.total_amount_cents || 0) -
        Number(invoice.paid_amount_cents || 0);

      return sum + Math.max(balance, 0);
    }, 0);

    return {
      totalPaidCents,
      totalInvoiceCents,
      openCount: openInvoices.length,
      openBalanceCents,
      total: invoices.length,
    };
  }, [invoices]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
  };

  const hasFilters = search.trim() || statusFilter !== "ALL";

  if (error && !invoices.length && !loading) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={loadInvoices}
              className={`${btnBase} border border-rose-200 bg-rose-100 px-3 py-2 text-rose-700 hover:bg-rose-200`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Billing Records
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Invoices
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  View invoice records, customer billing periods, payment
                  amounts, and invoice status history.
                </p>
              </div>

              <button
                type="button"
                onClick={loadInvoices}
                disabled={loading || refreshing}
                className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {refreshing || loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing || loading ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Inline refresh/error */}
        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing invoice records…
          </div>
        )}

        {error && invoices.length > 0 && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Paid Revenue"
            value={formatKES(stats.totalPaidCents)}
            sub="Total paid amount"
            icon={<TrendingUp className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Open Invoices"
            value={stats.openCount}
            sub="Pending, expired or cancelled"
            icon={<Clock className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Open Balance"
            value={formatKES(stats.openBalanceCents)}
            sub="Outstanding invoice balance"
            icon={<Banknote className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Total Invoices"
            value={stats.total}
            sub="All invoice records"
            icon={<FileText className="h-5 w-5" />}
            tone="slate"
          />
        </div>

        {/* Main panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Receipt className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Invoice History
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  {loading
                    ? "Loading invoice data…"
                    : `${filtered.length} of ${invoices.length} invoices`}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search invoice, customer, plan…"
                  className={`${inputCls} pl-10`}
                />
              </div>

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Status filter tabs */}
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {STATUS_TABS.map((tab) => {
                const count =
                  tab.value === "ALL"
                    ? invoices.length
                    : invoices.filter((invoice) => invoice.status === tab.value)
                        .length;

                const active = statusFilter === tab.value;

                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-2xl border px-3.5 py-2 text-[12px] font-black transition-all ${
                      active
                        ? "border-emerald-200 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20"
                        : "border-slate-300 bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                    }`}
                  >
                    {tab.label}

                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                        active
                          ? "bg-white/20 text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Invoice #</th>
                  <th className="px-5 py-3.5 font-black">Customer</th>
                  <th className="px-5 py-3.5 font-black">Plan</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 text-right font-black">Total</th>
                  <th className="px-5 py-3.5 text-right font-black">Paid</th>
                  <th className="px-5 py-3.5 font-black">Period</th>
                  <th className="px-5 py-3.5 font-black">Created</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonRow key={index} />
                  ))
                ) : filtered.length > 0 ? (
                  filtered.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-4">
                        <p className="font-mono text-[12.5px] font-black text-slate-800">
                          {invoice.invoice_number}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-[13px] font-bold text-slate-700">
                        {invoice.secret_username || "—"}
                      </td>

                      <td className="px-5 py-4">
                        {invoice.plan_name ? (
                          <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                            {invoice.plan_name}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <StatusPill status={invoice.status} />
                      </td>

                      <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-slate-800">
                        {formatKES(invoice.total_amount_cents)}
                      </td>

                      <td className="px-5 py-4 text-right font-mono text-[12.5px] font-bold text-emerald-700">
                        {formatKES(invoice.paid_amount_cents)}
                      </td>

                      <td className="px-5 py-4 text-[12.5px] font-medium text-slate-500">
                        {invoice.period_start ? (
                          <span className="flex items-center gap-1.5">
                            <span>{formatDate(invoice.period_start)}</span>
                            <span className="text-slate-300">→</span>
                            <span>{formatDate(invoice.period_end)}</span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="px-5 py-4 text-[12.5px] font-medium text-slate-500">
                        {formatDate(invoice.created_at)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Search className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No invoices found
                      </p>

                      <p className="mx-auto mt-1 max-w-xs text-[13px] font-medium text-slate-500">
                        Try adjusting your search or status filter.
                      </p>

                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filtered.length > 0 ? (
              filtered.map((invoice) => (
                <div key={invoice.id} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-[13px] font-black text-slate-900">
                        {invoice.invoice_number}
                      </p>

                      <p className="mt-0.5 truncate text-[12px] font-bold text-slate-500">
                        {invoice.secret_username || "Unknown customer"}
                      </p>

                      {invoice.plan_name && (
                        <span className="mt-2 inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[10.5px] font-black text-sky-800">
                          {invoice.plan_name}
                        </span>
                      )}
                    </div>

                    <StatusPill status={invoice.status} />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Total
                      </p>
                      <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                        {formatKES(invoice.total_amount_cents)}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                        Paid
                      </p>
                      <p className="mt-1 font-mono text-[12.5px] font-black text-emerald-800">
                        {formatKES(invoice.paid_amount_cents)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Period
                    </p>

                    <p className="mt-1 text-[12px] font-medium text-slate-600">
                      {invoice.period_start
                        ? `${formatDate(invoice.period_start)} → ${formatDate(
                            invoice.period_end,
                          )}`
                        : "—"}
                    </p>

                    <p className="mt-1 text-[11px] font-medium text-slate-400">
                      Created: {formatDate(invoice.created_at)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Search className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No invoices found
                </p>

                <p className="mx-auto mt-1 max-w-xs text-[13px] font-medium text-slate-500">
                  Try adjusting your search or status filter.
                </p>

                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {invoices.length}
              </span>{" "}
              invoices
            </p>

            {statusFilter !== "ALL" && (
              <button
                type="button"
                onClick={() => setStatusFilter("ALL")}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Show all invoices
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
